import { registerSW } from 'virtual:pwa-register';
import { createVersionTracker, parseVersion } from './state';
import { showUpdateGate } from './gate';

declare const __APP_VERSION__: string;
const STORAGE_KEY = 'bmail-update-attempts';
export function registerUpdateGate() {
  let previous: string[] = [];
  try {
    const saved: unknown = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
    if (Array.isArray(saved)) previous = saved.filter((v): v is string => typeof v === 'string');
  } catch { /* Private browsing/storage restrictions must not disable updates. */ }
  const tracker = createVersionTracker(__APP_VERSION__, previous);
  let registration: ServiceWorkerRegistration | undefined;
  let waiting = false;
  let shown = false;
  let checking = false;
  const offer = () => {
    if (shown || !tracker.shouldGate(waiting || !!registration?.waiting)) return;
    shown = true;
    showUpdateGate(async () => {
      tracker.markAttempt();
      try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tracker.attempts())); } catch { /* best effort */ }
      // The deadline includes update(): an offline/hung request cannot strand Reload.
      if (!registration?.waiting) {
        void registration?.update().catch(() => {});
        const deadline = Date.now() + 5000;
        while (!registration?.waiting && Date.now() < deadline) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      if (registration?.waiting) await updateSW(true);
      else window.location.reload();
    });
  };
  const check = async () => {
    // Check the SW and metadata independently; neither can block the other.
    void registration?.update().catch(() => {});
    if (checking) return;
    checking = true;
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), 10_000);
    try {
      const response = await fetch('/app-version.json', { cache: 'no-store', signal: abort.signal });
      if (response.ok) {
        const remote = parseVersion(await response.json());
        if (remote) tracker.observe(remote);
      }
    } catch { /* Offline or unavailable metadata: SW detection still works. */ }
    finally { clearTimeout(timeout); checking = false; offer(); }
  };
  const updateSW = registerSW({
    immediate: true,
    onRegisteredSW(_url, value) {
      registration = value;
      void check();
    },
    onNeedRefresh() { waiting = true; offer(); }
  });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) void check(); });
  window.addEventListener('focus', () => { void check(); });
  window.addEventListener('online', () => { void check(); });
  setInterval(() => { if (!document.hidden) void check(); }, 60_000);
  void check();
}
